import { AcroNode } from "./types";
import { escapeHtml } from "./escapeHtml";
import { renderEditTextInputOnly } from "./renderEditText";
import { renderCheckbox } from "./renderCheckbox";

// Render groups with options (special-cased like ADL) in a simple layout.
export function groupWithOptions(node: AcroNode): string {
  // Generic rule: if id includes 'table' and all children are groups with options,
  // render a simplified checkbox layout (works for ADL, IADL, etc.).
  const idIncludesTable = String(node.id ?? "").toLowerCase().includes("table");
  const children = Array.isArray(node.children) ? node.children : [];
  const hasRowGroupsWithOptions =
    children.length > 0 &&
    children.every(
      (c) => String(c.type ?? "").toLowerCase() === "group" && Array.isArray(c.options) && c.options.length > 0
    );

  if (!(idIncludesTable && hasRowGroupsWithOptions)) return "";

  const titleHtml = node.title
    ? `<div class="group-with-options-title basis-full text-sm font-semibold">${escapeHtml(node.title)}</div>`
    : "";

  const childBlocks = children
    .slice()
    .map((child) => {
      const childTitle = child.title
        ? `<div class="group-with-options-child-title basis-full text-sm font-medium">${escapeHtml(child.title)}</div>`
        : "";
      const childOptions: string[] = Array.isArray(child.options)
        ? (child.options as unknown[]).map((o) => String(o))
        : [];
      const childFieldId = child.field_id ? escapeHtml(String(child.field_id)) : "";
      const childFieldIdAttr = childFieldId ? ` data-field-id="${childFieldId}"` : "";
      const checkboxes = childOptions
        .map((opt: string, idx: number) => {
          const inputId = `${child.id}_option_${idx}`;
          return `<label class="flex items-center gap-2 mb-1 text-sm">
              <input type="checkbox" data-id="${escapeHtml(inputId)}"${childFieldIdAttr} class="form-checkbox" />
              <span>${escapeHtml(opt)}</span>
            </label>`;
        })
        .join("");
      const extras = Array.isArray(child.children)
        ? (child.children as AcroNode[])
          .slice()
          .map((gc) => {
            const t = String(gc.type ?? "").toLowerCase();
            if (t === "checkbox") return renderCheckbox(gc);
            if (t === "edittext") {
              const ph = (gc.title ?? gc.placeholder ?? "") as string;
              const nodeForInput: AcroNode = { ...gc, placeholder: ph };
              return renderEditTextInputOnly(nodeForInput);
            }
            return "";
          })
          .join("")
        : "";
      return `<div class="group-with-options-child flex flex-auto flex-wrap gap-2 py-3 px-4 border border-gray-300 rounded-lg">
                  ${childTitle}
                  <div class="group-with-options-child-options flex flex-wrap gap-2">
                  ${checkboxes}
                  </div>
                  ${extras ? `<div class="group-with-options-child-extras flex flex-col gap-2 flex-auto">${extras}</div>` : ""}
                </div>`;
    })
    .join("");

  return `<div class="group-with-options flex flex-wrap gap-4">${titleHtml}${childBlocks}</div>`;
}